<template>
  <div v-show="visible" class="roocode-at-panel" :style="panelStyle">
    <!-- 一级面板 -->
    <div v-if="!selectedCategory" class="panel-level-one">
      <div class="panel-header">选择类型</div>
      <div class="panel-items">
        <div
          v-if="groupMembers && groupMembers.length > 0"
          class="panel-item"
          :class="{ active: activeIndex === 0 }"
          @click="selectCategory('members')"
          @mouseenter="activeIndex = 0"
        >
          <i class="el-icon-user"></i>
          <span>群成员</span>
          <i class="el-icon-arrow-right"></i>
        </div>
        <div
          class="panel-item"
          :class="{ active: activeIndex === (groupMembers && groupMembers.length > 0 ? 1 : 0) }"
          @click="selectCategory('agents')"
          @mouseenter="activeIndex = (groupMembers && groupMembers.length > 0 ? 1 : 0)"
        >
          <i class="el-icon-cpu"></i>
          <span>智能体</span>
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>
    </div>

    <!-- 二级面板 -->
    <div v-else class="panel-level-two">
      <div class="panel-header">
        <span @click="backToFirstLevel" class="back-btn">
          <i class="el-icon-arrow-left"></i>
        </span>
        {{ selectedCategory === 'members' ? '选择群成员' : '选择智能体' }}
      </div>
      
      <div class="panel-search">
        <input 
          v-model="searchText" 
          :placeholder="selectedCategory === 'agents' ? '搜索智能体...' : '搜索...'"
          class="search-input"
          @input="filterItems"
        />
      </div>

      <div class="panel-items">
        <div v-if="loading" class="loading">
          <i class="el-icon-loading"></i> 加载中...
        </div>
        
        <div v-else-if="filteredItems.length === 0 && usedAgents.length === 0" class="empty">
          {{ selectedCategory === 'members' ? '暂无群成员' : '暂无智能体' }}
        </div>
        
        <template v-else>
          <!-- 当前会话智能体 section -->
          <template v-if="selectedCategory === 'agents' && filteredItems.length > 0">
            <div class="section-header" v-if="usedAgents.length > 0">当前会话智能体</div>
            <div
              v-for="(item, index) in filteredItems"
              :key="item.agentId"
              class="panel-item"
              :class="{
                active: activeIndex === index,
                selected: isItemSelected(item)
              }"
              @click="selectItem(item)"
              @mouseenter="activeIndex = index"
            >
              <img v-if="item.avatarUrl" :src="item.avatarUrl" class="item-avatar" />
              <i v-else class="el-icon-cpu"></i>
              <div class="item-content">
                <div class="item-name">{{ item.agentName }}</div>
                <div v-if="item.description" class="item-desc">{{ item.description }}</div>
              </div>
              <i v-if="isItemSelected(item)" class="el-icon-check selected-mark"></i>
            </div>
          </template>

          <!-- 用过的智能体 section -->
          <template v-if="selectedCategory === 'agents' && usedAgents.length > 0">
            <div class="section-header">用过的智能体</div>
            <div
              v-for="(item, index) in usedAgents"
              :key="`used-${item.agentId}`"
              class="panel-item"
              :class="{
                active: activeIndex === (filteredItems.length + index),
                selected: isItemSelected(item)
              }"
              @click="selectItem(item)"
              @mouseenter="activeIndex = (filteredItems.length + index)"
            >
              <img v-if="item.avatarUrl" :src="item.avatarUrl" class="item-avatar" />
              <i v-else class="el-icon-cpu"></i>
              <div class="item-content">
                <div class="item-name">{{ item.agentName }}</div>
                <div v-if="item.description" class="item-desc">{{ item.description }}</div>
              </div>
              <i v-if="isItemSelected(item)" class="el-icon-check selected-mark"></i>
            </div>
          </template>

          <!-- 群成员 -->
          <template v-if="selectedCategory === 'members'">
            <div
              v-for="(item, index) in filteredItems"
              :key="item.userId"
              class="panel-item"
              :class="{
                active: activeIndex === index,
                selected: isItemSelected(item)
              }"
              @click="selectItem(item)"
              @mouseenter="activeIndex = index"
            >
              <img v-if="item.avatarUrl" :src="item.avatarUrl" class="item-avatar" />
              <i v-else :class="item.iconClass || 'el-icon-user'"></i>
              <div class="item-content">
                <div class="item-name">{{ item.name }}</div>
                <div v-if="item.description" class="item-desc">{{ item.description }}</div>
              </div>
              <i v-if="isItemSelected(item)" class="el-icon-check selected-mark"></i>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RooCodeAtPanel',
  props: {
    userId: {
      type: Number,
      required: true
    },
    targetUserId: {
      type: [Number, String],
      default: null
    },
    groupMembers: {
      type: Array,
      default: () => []
    },
    isGroup: {
      type: Boolean,
      default: false
    },
    // 新增：从父组件接收智能体列表
    agents: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      visible: false,
      position: { x: 0, y: 0 },
      selectedCategory: null,
      items: [],
      filteredItems: [],
      usedAgents: [], // 用过的智能体列表
      searchText: '',
      activeIndex: 0,
      loading: false,
      callback: null,
      currentSelection: null, // 保存当前选中的项目信息
      openTime: 0, // 记录面板打开时间，避免立即被关闭
      openedFromIndicator: false, // 标记是否从@指示器打开
      clickCheckInterval: null // 点击检查定时器
    }
  },

  computed: {
    panelStyle() {
      // 面板高度（根据是否显示搜索框动态调整）
      const panelHeight = this.selectedCategory && this.filteredItems.length > 5 ? 400 : 350;
      
      // 根据是否需要显示在下方调整位置
      if (this.position.isBelow) {
        // 显示在输入框下方
        return {
          left: `${this.position.x}px`,
          top: `${this.position.y + 20}px`,
          maxHeight: `${Math.min(panelHeight, window.innerHeight - this.position.y - 50)}px`
        }
      } else {
        // 默认显示在输入框上方
        const availableHeight = this.position.y - 20;
        return {
          left: `${this.position.x}px`,
          bottom: `${window.innerHeight - this.position.y + 5}px`,
          maxHeight: `${Math.min(panelHeight, availableHeight)}px`
        }
      }
    }
  },
  methods: {
    open(position, callback, existingSelection = null, searchText = '') {
      // 先移除任何现有的事件监听器
      this.removeEventListeners();
      
      this.visible = true;
      this.openTime = Date.now(); // 记录打开时间
      this.openedFromIndicator = !!existingSelection; // 如果有现有选择，说明是从指示器打开的
      
      // 调整位置，确保面板不会超出屏幕左右边界
      let adjustedX = position.x;
      const panelWidth = 400; // 最大宽度
      
      // 如果面板会超出右边界，调整到左边
      if (adjustedX + panelWidth > window.innerWidth) {
        adjustedX = window.innerWidth - panelWidth - 10;
      }
      
      // 如果面板会超出左边界，调整到右边
      if (adjustedX < 10) {
        adjustedX = 10;
      }
      
      this.position = {
        x: adjustedX,
        y: position.y
      };
      
      this.callback = callback;
      
      // 如果有现有选择，直接跳转到对应的二级面板
      if (existingSelection) {
        this.currentSelection = existingSelection;
        this.selectedCategory = existingSelection.type;
        this.searchText = '';

        // 加载对应分类的数据
        if (existingSelection.type === 'members') {
          this.loadMembers();
        } else {
          this.loadAgents();
        }

        // 等待数据加载后定位到选中项
        this.$nextTick(() => {
          this.locateSelectedItem();
        });
      } else if (searchText) {
        // 根据搜索文本自动选择分类
        const lowerSearch = searchText.toLowerCase();
        if (lowerSearch.includes('智能体') || lowerSearch.includes('agent') || lowerSearch.includes('ai')) {
          this.selectCategory('agents');
          this.currentSelection = null;
        } else {
          // 其他文本，显示一级面板
          this.selectedCategory = null;
          this.activeIndex = 0;
          this.searchText = searchText;
          this.currentSelection = null;
        }
      } else {
        // 新的选择，重置状态
        this.selectedCategory = null;
        this.activeIndex = 0;
        this.searchText = '';
        this.currentSelection = null;
      }
      
      // 添加事件监听器
      this.addEventListeners();
      
      // 在下一个tick中检查面板位置，确保不会超出顶部
      this.$nextTick(() => {
        this.adjustPanelPosition();
      });
    },
    
    // 添加事件监听器的统一方法
    addEventListeners() {
      // 监听键盘事件
      document.addEventListener('keydown', this.handleKeydown);
      
      if (this.openedFromIndicator) {
        // 从指示器打开的面板，使用轮询方式检测外部点击
        console.log('[DEBUG] Adding polling-based click detection for indicator-opened panel');
        this.startClickPolling();
      } else {
        // 正常打开的面板，使用常规事件监听
        this.$nextTick(() => {
          if (this.visible) {
            document.addEventListener('click', this.handleClickOutside, true);
            console.log('[DEBUG] Click outside listener added (capture mode)');
          }
        });
      }
    },
    
    // 开始轮询检测点击
    startClickPolling() {
      // 清除现有的定时器
      if (this.clickCheckInterval) {
        clearInterval(this.clickCheckInterval);
      }
      
      let lastClickTime = 0;
      let ignoreClicksUntil = Date.now() + 300; // 忽略面板打开后300ms内的点击
      
      // 添加一个全局点击监听器来更新最后点击时间
      const updateClickTime = (e) => {
        const now = Date.now();
        
        // 忽略面板打开后的初始点击
        if (now < ignoreClicksUntil) {
          console.log('[DEBUG] Ignoring click during grace period');
          return;
        }
        
        // 忽略面板内的点击
        if (this.$el && this.$el.contains(e.target)) {
          console.log('[DEBUG] Ignoring click inside panel');
          return;
        }
        
        console.log('[DEBUG] Recording outside click for polling detection');
        lastClickTime = now;
      };
      
      document.addEventListener('mousedown', updateClickTime, true);
      
      // 定期检查是否有新的外部点击事件
      this.clickCheckInterval = setInterval(() => {
        if (!this.visible) {
          clearInterval(this.clickCheckInterval);
          document.removeEventListener('mousedown', updateClickTime, true);
          return;
        }
        
        // 检查是否有新的外部点击（最近100ms内）
        if (lastClickTime > 0 && Date.now() - lastClickTime < 100) {
          console.log('[DEBUG] Polling detected valid outside click, closing panel');
          this.close();
          clearInterval(this.clickCheckInterval);
          document.removeEventListener('mousedown', updateClickTime, true);
        }
      }, 50);
    },
    
    // 检查最近的点击是否在面板内
    isClickInPanel() {
      const activeElement = document.activeElement;
      return this.$el && (this.$el.contains(activeElement) || this.$el === activeElement);
    },
    
    // 移除事件监听器的统一方法
    removeEventListeners() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('click', this.handleClickOutside, true); // 捕获模式
      document.removeEventListener('click', this.handleClickOutside, false); // 冒泡模式
      
      // 清除轮询定时器
      if (this.clickCheckInterval) {
        clearInterval(this.clickCheckInterval);
        this.clickCheckInterval = null;
      }
      
      console.log('[DEBUG] Event listeners removed');
    },
    
    // 定位到当前选中的项目
    locateSelectedItem() {
      if (!this.currentSelection || !this.filteredItems.length) return;
      
      // 查找当前选择的项目在列表中的索引
      const index = this.filteredItems.findIndex(item => {
        if (this.currentSelection.type === 'members') {
          return item.userId === this.currentSelection.item.userId;
        } else {
          return item.id === this.currentSelection.item.id || 
                 item.slug === this.currentSelection.item.slug;
        }
      });
      
      if (index >= 0) {
        this.activeIndex = index;
        this.scrollToActiveItem();
      }
    },
    
    adjustPanelPosition() {
      const panel = this.$el;
      if (!panel) return;
      
      const rect = panel.getBoundingClientRect();
      const panelHeight = rect.height;
      
      // 如果面板顶部超出屏幕，调整位置
      if (rect.top < 10) {
        // 显示在输入框下方
        this.$set(this, 'position', {
          ...this.position,
          isBelow: true
        });
      }
    },
    
    close() {
      this.visible = false;
      this.selectedCategory = null;
      this.items = [];
      this.filteredItems = [];
      this.removeEventListeners();
    },
    
    // 处理点击外部事件
    handleClickOutside(e) {
      console.log('[DEBUG] handleClickOutside triggered, target:', e.target);
      
      // 如果面板不可见，不处理
      if (!this.visible) {
        console.log('[DEBUG] handleClickOutside - panel not visible, ignoring');
        return;
      }
      
      // 如果点击的是面板本身或其子元素，不关闭
      if (this.$el && this.$el.contains(e.target)) {
        console.log('[DEBUG] handleClickOutside - clicked inside panel, ignoring');
        return;
      }
      
      // 如果点击的是触发打开面板的元素（如@指示器），不关闭
      if (e.target.classList && (
        e.target.classList.contains('roocode-mention') ||
        e.target.classList.contains('chat-at-user')
      )) {
        console.log('[DEBUG] handleClickOutside - clicked on mention, ignoring');
        return;
      }
      
      // 检查是否点击了RooCode mention相关的元素（通过文本内容判断）
      if (e.target.innerText && (
        e.target.innerText.startsWith('@任务[') ||
        e.target.innerText.startsWith('@智能体[')
      )) {
        console.log('[DEBUG] handleClickOutside - clicked on RooCode mention, ignoring');
        return;
      }
      
      // 如果点击的是输入框，也不关闭（用户可能在继续输入）
      if (e.target.closest('.content')) {
        console.log('[DEBUG] handleClickOutside - clicked in input area, ignoring');
        return;
      }
      
      console.log('[DEBUG] handleClickOutside - closing panel');
      // 点击其他区域，关闭面板
      this.close();
    },
    
    handleKeydown(e) {
      if (!this.visible) return;
      
      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.moveUp();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.moveDown();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.confirmSelection();
      }
    },
    
    moveUp() {
      if (this.activeIndex > 0) {
        this.activeIndex--;
        this.scrollToActiveItem();
      }
    },
    
    moveDown() {
      let maxIndex;
      if (this.selectedCategory) {
        // 二级面板：计算总项目数（当前会话的 + 用过的）
        if (this.selectedCategory === 'agents') {
          maxIndex = this.filteredItems.length + this.usedAgents.length - 1;
        } else {
          maxIndex = this.filteredItems.length - 1;
        }
      } else {
        // 一级面板的最大索引取决于是否有群成员
        maxIndex = (this.groupMembers && this.groupMembers.length > 0) ? 2 : 1;
      }
      if (this.activeIndex < maxIndex) {
        this.activeIndex++;
        this.scrollToActiveItem();
      }
    },
    
    scrollToActiveItem() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.panel-items');
        const activeItem = this.$el.querySelectorAll('.panel-item')[this.activeIndex];
        
        if (container && activeItem) {
          const containerRect = container.getBoundingClientRect();
          const itemRect = activeItem.getBoundingClientRect();
          
          // 如果项目在容器上方，滚动到顶部
          if (itemRect.top < containerRect.top) {
            container.scrollTop -= (containerRect.top - itemRect.top + 10);
          }
          // 如果项目在容器下方，滚动到底部
          else if (itemRect.bottom > containerRect.bottom) {
            container.scrollTop += (itemRect.bottom - containerRect.bottom + 10);
          }
        }
      });
    },
    
    confirmSelection() {
      if (!this.selectedCategory) {
        // 在一级面板
        const hasMembers = this.groupMembers && this.groupMembers.length > 0;
        if (hasMembers) {
          // 有群成员时的选择
          if (this.activeIndex === 0) {
            this.selectCategory('members');
          } else {
            this.selectCategory('agents');
          }
        } else {
          // 没有群成员时，只有智能体选项
          this.selectCategory('agents');
        }
      } else {
        // 在二级面板
        if (this.selectedCategory === 'agents') {
          // 智能体面板：当前会话优先，然后是用过的
          const currentCount = this.filteredItems.length;
          if (this.activeIndex < currentCount) {
            // 选择当前会话智能体
            this.selectItem(this.filteredItems[this.activeIndex]);
          } else {
            // 选择用过的智能体
            const usedIndex = this.activeIndex - currentCount;
            if (this.usedAgents[usedIndex]) {
              this.selectItem(this.usedAgents[usedIndex]);
            }
          }
        } else {
          // 群成员面板
          if (this.filteredItems[this.activeIndex]) {
            this.selectItem(this.filteredItems[this.activeIndex]);
          }
        }
      }
    },
    
    selectCategory(category) {
      console.log('[DEBUG] selectCategory called with:', category);
      this.selectedCategory = category;
      this.activeIndex = 0;
      this.items = [];
      this.filteredItems = [];

      if (category === 'members') {
        this.loadMembers();
      } else if (category === 'agents') {
        this.loadAgents();
      }

      this.$forceUpdate();
    },
    
    backToFirstLevel() {
      this.selectedCategory = null;
      this.activeIndex = 0;
      this.items = [];
      this.filteredItems = [];
    },
    
    // 加载智能体列表（使用 props 传入的数据）
    loadAgents() {
      this.loading = false;
      // 过滤已发布的智能体
      const publishedAgents = (this.agents || []).filter(
        agent => agent.publishStatus === 'PUBLISHED'
      );
      this.items = publishedAgents;
      this.filteredItems = [...this.items];
      
      // 加载用过的智能体
      this.loadUsedAgents();
    },

    // 加载用过的智能体（从已创建的智能体会话中提取）
    loadUsedAgents() {
      try {
        // 从 store 获取所有会话
        const allChats = this.$store.state.chatStore.chats || [];
        
        // 筛选出智能体会话
        const agentChats = allChats.filter(chat => chat.type === 'AGENT');
        
        // 提取智能体信息并去重
        const agentMap = new Map();
        
        agentChats.forEach(chat => {
          // 从 targetId 提取智能体 ID
          const parts = chat.targetId.split('_agent_');
          if (parts.length === 2) {
            const agentId = parts[1];
            
            // 如果已经在当前会话智能体列表中，跳过
            const isInCurrentList = this.items.some(item => 
              String(item.agentId) === String(agentId)
            );
            
            if (!isInCurrentList && !agentMap.has(agentId)) {
              agentMap.set(agentId, {
                agentId: agentId,
                agentName: chat.agentName || '未知智能体',
                description: chat.description || '',
                avatarUrl: chat.headImage || '',
                lastUseTime: chat.lastSendTime || 0
              });
            }
          }
        });
        
        // 转换为数组并按最后使用时间排序
        this.usedAgents = Array.from(agentMap.values())
          .sort((a, b) => b.lastUseTime - a.lastUseTime);
        
        console.log('[RooCodeAtPanel] 加载用过的智能体:', this.usedAgents.length);
      } catch (error) {
        console.error('[RooCodeAtPanel] 加载用过的智能体失败:', error);
        this.usedAgents = [];
      }
    },

    loadMembers() {
      this.loading = false;
      this.items = this.groupMembers.map(member => ({
        userId: member.userId,
        name: member.showNickName || member.nickName,
        description: member.signature || '群成员',
        iconClass: 'el-icon-user'
      }));
      this.filteredItems = [...this.items];
    },

    filterItems() {
      if (!this.searchText) {
        this.filteredItems = [...this.items];
        if (this.selectedCategory === 'agents') {
          this.usedAgents = this.loadUsedAgentsRaw();
        }
      } else {
        const search = this.searchText.toLowerCase();
        this.filteredItems = this.items.filter(item => {
          const name = item.agentName || item.name || '';
          return name.toLowerCase().includes(search) ||
            (item.description && item.description.toLowerCase().includes(search));
        });
        
        // 同时过滤用过的智能体
        if (this.selectedCategory === 'agents') {
          const allUsedAgents = this.loadUsedAgentsRaw();
          this.usedAgents = allUsedAgents.filter(item => {
            const name = item.agentName || '';
            return name.toLowerCase().includes(search) ||
              (item.description && item.description.toLowerCase().includes(search));
          });
        }
      }
      this.activeIndex = 0;
    },

    // 获取原始的用过的智能体列表（不过滤）
    loadUsedAgentsRaw() {
      try {
        const allChats = this.$store.state.chatStore.chats || [];
        const agentChats = allChats.filter(chat => chat.type === 'AGENT');
        const agentMap = new Map();
        
        agentChats.forEach(chat => {
          const parts = chat.targetId.split('_agent_');
          if (parts.length === 2) {
            const agentId = parts[1];
            const isInCurrentList = this.items.some(item => 
              String(item.agentId) === String(agentId)
            );
            
            if (!isInCurrentList && !agentMap.has(agentId)) {
              agentMap.set(agentId, {
                agentId: agentId,
                agentName: chat.agentName || '未知智能体',
                description: chat.description || '',
                avatarUrl: chat.headImage || '',
                lastUseTime: chat.lastSendTime || 0
              });
            }
          }
        });
        
        return Array.from(agentMap.values())
          .sort((a, b) => b.lastUseTime - a.lastUseTime);
      } catch (error) {
        console.error('[RooCodeAtPanel] 加载用过的智能体失败:', error);
        return [];
      }
    },

    // 判断项目是否为当前选中的项目
    isItemSelected(item) {
      if (!this.currentSelection || !this.currentSelection.item) return false;

      const selectedItem = this.currentSelection.item;
      if (this.selectedCategory === 'members') {
        return item.userId === selectedItem.userId;
      } else {
        return item.agentId === selectedItem.agentId;
      }
    },

    selectItem(item) {
      if (this.callback) {
        let displayText;

        if (this.selectedCategory === 'members') {
          // 群成员，保持原有格式
          displayText = `@${item.name}`;
        } else {
          // 智能体 - 使用纯文本格式
          displayText = `@智能体[${item.agentName}]`;
        }

        const result = {
          type: this.selectedCategory,
          item: item,
          displayText: displayText
        };

        // 保存当前选择
        this.currentSelection = result;

        this.callback(result);
      }
      this.close();
    },

    // 获取当前选择（供父组件调用）
    getCurrentSelection() {
      return this.currentSelection;
    },
    
    // 清除当前选择
    clearSelection() {
      this.currentSelection = null;
    }
  },
  
  beforeDestroy() {
    this.removeEventListeners();
  }
}
</script>

<style scoped lang="scss">
.roocode-at-panel {
  position: fixed;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 3000;
  min-width: 280px;
  max-width: 400px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .panel-header {
    padding: 12px 16px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;

    .back-btn {
      cursor: pointer;
      color: #606266;
      &:hover {
        color: #409EFF;
      }
    }
  }

  .panel-search {
    padding: 8px 16px;
    box-sizing: border-box;

    .search-input {
      width: 100%;
      padding: 6px 10px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      font-size: 14px;
      outline: none;
      box-sizing: border-box;

      &:focus {
        border-color: #409EFF;
      }
    }
  }

  .panel-items {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 4px 0;
    max-height: 300px;
    min-height: 100px;
    
    // 美化滚动条
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 3px;
      
      &:hover {
        background: #555;
      }
    }

    .section-header {
      padding: 6px 16px;
      font-size: 12px;
      color: #909399;
      font-weight: 500;
      text-align: left;
    }

    .panel-item {
      padding: 10px 16px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 10px;
      transition: background-color 0.2s;
      width: 100%;
      box-sizing: border-box;
      text-align: left;

      &:hover, &.active {
        background-color: #f5f7fa;
      }

      &.active {
        background-color: #ecf5ff;
      }
      
      &.selected {
        background-color: #f0f9ff;
      }
      
      .selected-mark {
        color: #67c23a;
        font-size: 16px;
        margin-left: 8px;
        flex-shrink: 0;
      }

      i {
        font-size: 18px;
        color: #909399;
        flex-shrink: 0;
      }

      .item-icon {
        font-size: 18px;
        width: 20px;
        text-align: center;
        flex-shrink: 0;
      }

      .item-avatar {
        width: 24px;
        height: 24px;
        border-radius: 4px;
        flex-shrink: 0;
        object-fit: cover;
      }

      .item-content {
        flex: 1;
        min-width: 0;
        text-align: left;

        .item-name {
          font-size: 14px;
          color: #303133;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: left;
        }

        .item-desc {
          font-size: 12px;
          color: #909399;
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: left;
        }
      }

      .item-type {
        font-size: 12px;
        color: #909399;
        padding: 2px 6px;
        background: #f4f4f5;
        border-radius: 2px;
        margin-left: auto;
        margin-right: 0;
        flex-shrink: 0;
      }

      .el-icon-arrow-right {
        margin-left: auto;
        color: #c0c4cc;
      }
    }

    .loading, .empty {
      padding: 20px;
      text-align: center;
      color: #909399;
      font-size: 14px;
    }
  }
}
</style>